import { Box, Textarea } from "@chakra-ui/react";
import SharedSelect from "../../../../../../shared/components/Select";
import { EntityProfileSegmentedControl } from "../../../../../entityProfileModal/components/EntityProfilePreferenceControls";
import type { CaregiverStaffingPreferencesBody } from "../staffingPreferences.types";
import {
  caregiverCasesLengthOptions,
  caregiverDrivingTimeOptions,
  caregiverPatientsTypeOptions,
  caregiverSecondaryLanguageOptions,
  caregiverStaffingLanguageOptions,
  caregiverTransportationOptions,
  caregiverWorkTimeOptions,
  normalizeOptionalText,
} from "../staffingPreferences.utils";

export function CaregiverStaffingBooleanValue(props: {
  value: boolean | null;
  onChange?: (value: boolean | null) => void;
}) {
  return (
    <EntityProfileSegmentedControl
      options={[
        { label: "Yes", value: true },
        { label: "No", value: false },
        { label: "Unknown", value: null },
      ]}
      value={props.value}
      onChange={
        props.onChange === undefined
          ? undefined
          : (value) => props.onChange?.(typeof value === "boolean" ? value : null)
      }
    />
  );
}

type Props = {
  draft: CaregiverStaffingPreferencesBody;
  fieldKey: keyof CaregiverStaffingPreferencesBody;
  onChangeDraft: (draft: CaregiverStaffingPreferencesBody) => void;
  onCommitDraft: (draft: CaregiverStaffingPreferencesBody) => void;
};

export function CaregiverStaffingPreferenceEditor(props: Props) {
  const { draft, fieldKey } = props;
  const commitDraftField = <TKey extends keyof CaregiverStaffingPreferencesBody>(
    key: TKey,
    value: CaregiverStaffingPreferencesBody[TKey]
  ) => {
    const nextDraft = { ...draft, [key]: value };

    props.onChangeDraft(nextDraft);
    props.onCommitDraft(nextDraft);
  };

  if (fieldKey === "mainLanguage") {
    return (
      <Box minW="190px">
        <SharedSelect
          label="Select language"
          multiple={false}
          options={caregiverStaffingLanguageOptions}
          value={draft.mainLanguage}
          onChange={(value) => commitDraftField("mainLanguage", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "languages") {
    return (
      <Box minW="220px">
        <SharedSelect
          label="Select languages"
          multiple={true}
          options={caregiverSecondaryLanguageOptions}
          value={draft.languages}
          onChange={(value) => commitDraftField("languages", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "transportationType") {
    return (
      <Box minW="220px">
        <SharedSelect
          label="Select transportation"
          multiple={false}
          options={caregiverTransportationOptions}
          value={draft.transportationType}
          onChange={(value) => commitDraftField("transportationType", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "publicTransprotationTimeLimit") {
    return (
      <Box minW="150px">
        <SharedSelect
          label="Select minutes"
          multiple={false}
          options={caregiverDrivingTimeOptions}
          value={draft.publicTransprotationTimeLimit}
          onChange={(value) => commitDraftField("publicTransprotationTimeLimit", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "workTimes") {
    return (
      <Box minW="190px">
        <SharedSelect
          label="Select work time"
          multiple={false}
          options={caregiverWorkTimeOptions}
          value={draft.workTimes}
          onChange={(value) => commitDraftField("workTimes", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "casesLength") {
    return (
      <Box minW="240px">
        <SharedSelect
          label="Select case lengths"
          multiple={true}
          options={caregiverCasesLengthOptions}
          value={draft.casesLength}
          onChange={(value) => commitDraftField("casesLength", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "patientsTypes") {
    return (
      <Box minW="260px">
        <SharedSelect
          label="Select patient types"
          multiple={true}
          options={caregiverPatientsTypeOptions}
          value={draft.patientsTypes}
          onChange={(value) => commitDraftField("patientsTypes", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "studyingTimes" || fieldKey === "additionalInfo") {
    return (
      <Textarea
        minW={{ base: "220px", md: "320px" }}
        rows={3}
        size="sm"
        value={draft[fieldKey] ?? ""}
        onBlur={(event) => {
          props.onCommitDraft({
            ...draft,
            [fieldKey]: normalizeOptionalText(event.currentTarget.value),
          });
        }}
        onChange={(event) =>
          props.onChangeDraft({
            ...draft,
            [fieldKey]: event.target.value,
          })
        }
      />
    );
  }

  return null;
}
